import {
  AuthRpcClient,
  createAuthRpcClient,
} from "@redotech/auth-sdk/rpc/client";
import {
  createRedoMerchantRpcClient,
  RedoMerchantRpcClient,
} from "@redotech/merchant-sdk/rpc/client";
import { isMockModeEnabled } from "@redotech/rpc-mock/mock-storage";
import { Store } from "@redotech/util/store";
import { epochSecondsToInstant } from "@redotech/util/temporal";
import { localStore, storageKeys } from "@redotech/web-util/storage";
import jwtDecode from "jwt-decode";
import {
  Context,
  createContext,
  memo,
  ReactNode,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { clientVersionHeaders } from "./client-version-headers";
import { REDO_API_URL, REDO_MERCHANT_SERVER_URL } from "./config";

export interface Auth {
  expiration: Temporal.Instant;
  isAdminImpersonation: boolean;
  teamId: string;
  userId: string;
  token: string;
}

export const AuthContext = createContext<Auth | undefined>(undefined);

export type SetAuth = (auth: Auth | undefined) => void;

export const SetAuthContext = createContext<SetAuth | undefined>(undefined);

export const TOKEN_KEY = "redo.merchant_auth_token";

/**
 * Local storage for team auth token.
 */
export function teamAuthStore(teamId: string): Store<string | undefined> {
  return localStore(`${TOKEN_KEY}.${teamId}`);
}

export function teamIds(): string[] {
  return [...storageKeys(localStorage)]
    .filter((key) => key.startsWith(`${TOKEN_KEY}.`))
    .map((key) => key.replace(`${TOKEN_KEY}.`, ""));
}

export function parseAuth(token: string): Auth | undefined {
  try {
    const decoded = jwtDecode<any>(token);
    const teamId = decoded.team_id ?? decoded.aud.split("/")[1];
    const userId = decoded.id ?? decoded.sub;
    if (
      !teamId ||
      typeof teamId !== "string" ||
      !userId ||
      typeof userId !== "string"
    ) {
      throw new TypeError("Not a valid Auth object");
      // Do not print sensitive information - only uncomment this to test locally
      // throw new TypeError(`Not a valid Auth object: ${JSON.stringify(decoded)}`);
    }
    return {
      expiration: epochSecondsToInstant(decoded.exp),
      isAdminImpersonation: decoded.src === "admin_login",
      teamId,
      token,
      userId,
    };
  } catch (error) {
    console.error("Failed to parse token", error);
    // Do not print sensitive information - only uncomment this to test locally
    // console.error(`Failed to parse invalid token ${token}`, error);
    return undefined;
  }
}

/** @returns whether the auth is expired (or expires within the next second) */
function isExpired(expiration: Temporal.Instant): boolean {
  const duration = expiration.since(Temporal.Now.instant());
  // Give ourselves 1 second leeway so it doesn't expire while trying to load the page
  return (
    Temporal.Duration.compare(
      Temporal.Duration.from({ seconds: 1 }),
      duration,
    ) > 0
  );
}

export const RedoMerchantUnauthenticatedAuthRpcClientContext: Context<
  AuthRpcClient | undefined
> = createContext<AuthRpcClient | undefined>(undefined);
RedoMerchantUnauthenticatedAuthRpcClientContext.displayName =
  "RedoMerchantAuthRpcClientContext";

// Session-less merchant RPC client for the public signup endpoints.
export const RedoMerchantAnonymousRpcClientContext: Context<
  RedoMerchantRpcClient | undefined
> = createContext<RedoMerchantRpcClient | undefined>(undefined);
RedoMerchantAnonymousRpcClientContext.displayName =
  "RedoMerchantAnonymousRpcClientContext";

export const RedoMerchantAuthRpcClientContext: Context<
  AuthRpcClient | undefined
> = createContext<AuthRpcClient | undefined>(undefined);
RedoMerchantAuthRpcClientContext.displayName =
  "RedoMerchantAuthRpcClientContext";

export const AuthRequired = memo(function AuthRequired({
  children,
}: {
  children: ReactNode | ReactNode[];
}) {
  const auth = useContext(AuthContext);
  const navigate = useNavigate();
  const location = useLocation();

  useLayoutEffect(() => {
    if (!auth) {
      const next = `${location.pathname}${location.search}${location.hash}`;
      navigate(`/login?next=${encodeURIComponent(next)}`);
    }
    // FIXME
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth]);

  return auth ? <>{children}</> : null;
});

export const AuthProvider = memo(function AuthProvider({
  children,
}: {
  children: ReactNode | ReactNode[];
}) {
  const parts = location.pathname.split("/");
  let teamId = parts[1] === "stores" ? parts[2] : null;
  let authStore = teamId ? teamAuthStore(teamId) : localStore(TOKEN_KEY); // TODO(paul): Fallback unnecessary now?

  const [auth, setAuth] = useState<Auth | undefined>(() => {
    // Delete expired or invalid tokens
    for (const key of storageKeys(localStorage)) {
      if (!key.startsWith(`${TOKEN_KEY}.`)) {
        continue;
      }
      const store = localStore(key);
      const parsed = parseAuth(store.get()!);
      if (parsed && !isExpired(parsed.expiration)) {
        continue;
      }
      store.set(undefined);
    }
    // Fetch the team's token
    const token = authStore.get();
    if (!token) {
      if (isMockModeEnabled()) {
        // Valid ObjectId hex (matching mockTeamData/mockUserData) so code that
        // parses auth ids with ObjectId/MongoId constructors doesn't throw.
        return {
          expiration: Temporal.Now.instant().add({ hours: 24 * 365 }),
          isAdminImpersonation: false,
          teamId: "000000000000000000000002",
          userId: "000000000000000000000001",
          token: "mock-token",
        };
      }
      return;
    }
    return parseAuth(token);
  });

  const redoMerchantUnauthenticatedAuthClient = createAuthRpcClient({
    baseURL: new URL(`${REDO_API_URL}/auth-rpc`),
  });

  const redoMerchantAnonymousRpcClient = useMemo(
    () =>
      createRedoMerchantRpcClient({
        baseURL: new URL(`${REDO_MERCHANT_SERVER_URL}/rpc`),
        headers: { ...clientVersionHeaders },
      }),
    [],
  );

  const redoMerchantAuthClient = useMemo(() => {
    if (!auth) {
      return undefined;
    }
    return createAuthRpcClient({
      baseURL: new URL(`${REDO_API_URL}/auth-rpc`),
      headers: { Authorization: `Bearer ${auth.token}` },
    });
  }, [auth]);

  useEffect(() => {
    // Clear the local storage if the auth is undefined
    if (!auth) {
      authStore.set(undefined);
      return;
    }
    if (!teamId) {
      // FIXME
      // eslint-disable-next-line react-hooks/exhaustive-deps
      teamId = auth.teamId;
      // FIXME
      // eslint-disable-next-line react-hooks/exhaustive-deps
      authStore = teamId ? teamAuthStore(teamId) : localStore(TOKEN_KEY);
    }
    authStore.set(auth.token);

    // Delete the auth when it expires
    const duration = auth.expiration.since(Temporal.Now.instant());
    const timeout = setTimeout(
      () => setAuth(undefined),
      Math.min(2_147_483_647, duration.total("milliseconds")),
    );
    return () => clearTimeout(timeout);
  }, [auth]);

  return (
    <AuthContext.Provider value={auth}>
      <SetAuthContext.Provider value={setAuth}>
        <RedoMerchantUnauthenticatedAuthRpcClientContext.Provider
          value={redoMerchantUnauthenticatedAuthClient}
        >
          <RedoMerchantAnonymousRpcClientContext.Provider
            value={redoMerchantAnonymousRpcClient}
          >
            <RedoMerchantAuthRpcClientContext.Provider
              value={redoMerchantAuthClient}
            >
              {children}
            </RedoMerchantAuthRpcClientContext.Provider>
          </RedoMerchantAnonymousRpcClientContext.Provider>
        </RedoMerchantUnauthenticatedAuthRpcClientContext.Provider>
      </SetAuthContext.Provider>
    </AuthContext.Provider>
  );
});
